import ManagerRepository from 'coreClasses/ManagerRepository';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {cloneObject, getConfig} from 'coreUtils/utils';
import {registerModuleStateReset} from 'coreUtils/moduleState';
import {queryBuilderConfig} from 'brand/app/contenthub/queryBuilderConfig';

import CMSProvider from '../CMSProvider';
import ComponentsBuilder from './componentsBuilder';
import ContentHubClient from './ContentHubClient';
import queryBuilder from './QueryBuilder';
import {buildEntriesQueries, prepareEntriesRequest} from './entriesQuery';
import type {TEntriesConfig} from './entriesQuery';
import {wrapItemsAsCollection} from 'coreUtils/contenthubResponseNormalizer';
import {isEntryInspectorEnabled} from 'coreUtils/livePreview';
import {resolveV2Includes} from 'coreUtils/resolveV2Includes';
import type {ICpaConnection} from 'coreUtils/cms/contentHubConnection';

const fetchPromises = new Map();
const cache = new Map();
const localeRegex = /^(?!id$)[a-z]{2}([-_][A-Z]{2})?$/;
const breakpointsExtendedList = new Set(ViewportMgr.getBreakpoints(true));

registerModuleStateReset(() => {
	fetchPromises.clear();
	cache.clear();
});

/**
 * Represents ContentHubV2Provider
 */
class ContentHubV2Provider extends CMSProvider {
	private componentsBuilder: ComponentsBuilder;
	private _fallbackLanguage: string;
	private _defaultLanguage: string;

	/**
	 * @constructor
	 * @param managerRepository {ManagerRepository} - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository);

		queryBuilder.setConfig(queryBuilderConfig);

		const uiMgr = managerRepository.getUiMgr();

		this.componentsBuilder = new ComponentsBuilder(
			uiMgr.isLivePreview() || (uiMgr.isCMSPreview() && isEntryInspectorEnabled())
		);
	}

	/**
	 * Returns provider ID
	 */
	getId(): string {
		return 'ContentHubV2Provider';
	}

	/**
	 * Returns the current language code formatted for CMS
	 */
	getCurrentLanguage() {
		return this.formatLocaleForCMS(this.getLanguageCode());
	}

	/**
	 * Returns the language code needed for contentful api call, overwritten if needed
	 * @returns {string} language code string
	 */
	private getLanguageCode() {
		const ISOLanguageCode = this.managerRepository.getLocalesMgr().getCurrentLocaleString('-');

		if (this.languageCodeNeedsOverride(ISOLanguageCode)) {
			return ISOLanguageCode;
		}

		return this.managerRepository.getLocalesMgr().getCurrentLanguage()?.getLanguageCode();
	}

	/**
	 * Checks if current language needs to be overwritten with custom value from configs
	 * @param {string} languageCode - language code that needs to be checked
	 * @returns {boolean} - true if language code needs to be overwritten
	 */
	private languageCodeNeedsOverride(languageCode: string) {
		const {app} = getConfig();
		const overrideLanguages = app.cmsAPI.overrideLanguages || [];

		return overrideLanguages?.includes(languageCode);
	}

	/**
	 * Placeholder
	 */
	getQueryAttributes() {
		return null;
	}

	/**
	 * Returns the fallback language code
	 */
	getFallbackLanguage() {
		if (!this._fallbackLanguage) {
			this._fallbackLanguage = this.formatLocaleForCMS(
				this.managerRepository.getLocalesMgr().getCurrentLanguage()?.getLanguageCode(),
			);
		}

		return this._fallbackLanguage;
	}

	/**
	 * Returns the default language code
	 */
	getDefaultLanguage() {
		if (!this._defaultLanguage) {
			this._defaultLanguage = this.managerRepository.getLocalesMgr().getDefaultLanguage().getLanguageCode();
		}

		return this._defaultLanguage;
	}

	/**
	 * Format locale for cms
	 * @param {String} languageCode
	 * @returns {String}
	 */
	formatLocaleForCMS(languageCode: string): string {
		return languageCode?.replace('_', '-');
	}

	/**
	 * Returns content
	 * @param {String} id Content Id
	 * @param {Object} config Additional config
	 * @param {Boolean} isMultiContent If yes returns an array of results
	 * @param oldLogic // Temporary, will be removed
	 * @returns {Promise}
	 */
	async getContent(
		id,
		config: TEntriesConfig = {},
		isMultiContent = false,
		oldLogic = true
	): Promise<any> {
		let result;
		const contentMgr = this.managerRepository.getContentMgr();
		const isPreview = contentMgr.isPreviewMode();
		const scheduledAt = isPreview ? contentMgr.getScheduledAt() : undefined;
		const cpaConnection = isPreview ? contentMgr.getCpaConnection() : undefined;
		const {variables, fallbackVariables, fetchAllLocales} = prepareEntriesRequest(id, config, this.getCurrentLanguage());

		// Concurrent server renders share `fetchPromises`: previews of different CMS connections must not
		const connectionKey = cpaConnection && [cpaConnection.host, cpaConnection.spaceId, cpaConnection.environmentId, cpaConnection.token].join('|');
		const cacheKey = JSON.stringify({id, variables, fallbackVariables, isPreview, fetchAllLocales, connectionKey, scheduledAt});

		if (!isPreview && cache.has(cacheKey)) {
			return cloneObject(cache.get(cacheKey));
		}

		let fetchPromise;

		if (fetchPromises.has(cacheKey)) {
			fetchPromise = fetchPromises.get(cacheKey);
		} else {
			fetchPromise = this.fetchEntries(id, variables, fallbackVariables, isPreview, scheduledAt, fetchAllLocales, cpaConnection);
		}

		fetchPromises.set(cacheKey, fetchPromise);

		try {
			result = await fetchPromise;
		} finally {
			// Also on failure: a rejected promise left here would fail every later request of this content.
			fetchPromises.delete(cacheKey);
		}

		if (oldLogic && result.items.length > 0) {
			if (result.includes) {
				resolveV2Includes(result);
			}

			const typeName = result?.items?.[0]?.__typename || id;

			result = wrapItemsAsCollection(typeName.charAt(0).toLowerCase() + typeName.slice(1), result.items, result.items.length);
		}

		// TODO: remove once switched all brands to V2
		if (result?.length === 0) {
			result = null;
		} else if (!isMultiContent && result?.length > 0) {
			result = result[0];
		} else if (result?.length === 1) {
			result = result[0];
		}

		if (!isPreview) {
			cache.set(cacheKey, cloneObject(result));
		}

		return result;
	}

	/**
	 * Performs the actual ContentHub API calls, including fallback attempts.
	 */
	private async fetchEntries(
		id: string,
		variables: Record<string, any>,
		fallbackVariables: Record<string, any>[],
		isPreview: boolean,
		scheduledAt: string | undefined,
		fetchAllLocales: boolean,
		cpaConnection?: ICpaConnection,
	): Promise<any> {
		const client = ContentHubClient.getClient(isPreview, cpaConnection);
		const {primary, fallbacks} = buildEntriesQueries(
			queryBuilder, id, {variables, fallbackVariables, fetchAllLocales}, {isPreview, scheduledAt},
		);

		let response = await client.getEntries(primary);

		if (response.total === 0) {
			for (const fallbackQuery of fallbacks) {
				response = await client.getEntries(fallbackQuery);

				if (response.total > 0) {
					break;
				}
			}
		}

		if (!response || response.sys?.type !== 'Array') {
			return [];
		}

		return response;
	}

	/**
	 * Parses localized field
	 * @param {object} field - Source field
	 */
	parseLocalizedField(field: object): object {
		if (!field) {
			return null;
		}

		const languages = [this.getCurrentLanguage(), this.getFallbackLanguage(), this.getDefaultLanguage()];

		for (let i = 0; i <= languages.length; i++) {
			const language = languages[i];

			if (
				typeof field[language] === 'boolean' ||
				typeof field[language] === 'number' ||
				typeof field[language] === 'string' ||
				field[language]
			) {
				return field[language];
			}
		}

		return null;
	}

	/**
	 * Parses localized content
	 * @param {object} content - Source content
	 */
	parseLocalizedContents(root: object): void {
		const stack: object[] = [root];

		while (stack.length > 0) {
			const content = stack.pop();

			if (Array.isArray(content)) {
				for (let i = 0; i < content.length; i++) {
					if (typeof content[i] === 'object' && content[i] !== null) {
						stack.push(content[i]);
					}
				}

				continue;
			}

			if (typeof content !== 'object' || content === null) {
				continue;
			}

			const contentKeys = Object.keys(content);

			for (let i = 0; i < contentKeys.length; i++) {
				const key = contentKeys[i];

				if (typeof content[key] !== 'object' || content[key] === null) {
					continue;
				}

				const res = this.parseLocalizedField(content[key]);

				if (res !== null) {
					content[key] = res;

					if (typeof res === 'object' && res !== null) {
						stack.push(res);
					}
				} else if (content[key]) {
					const fieldKeys = Object.keys(content[key]);
					const filteredKeys = fieldKeys.filter((k) => !breakpointsExtendedList.has(k));
					const isStillLocalizable =
						filteredKeys.length > 0 && filteredKeys.every((k) => localeRegex.test(k));

					if (isStillLocalizable) {
						delete content[key];
					} else {
						stack.push(content[key]);
					}
				}
			}
		}
	}

	/**
	 * Parses localized content
	 * @param {object} content - Source content
	 */
	parseLocalizedContent(content: {allLocales?: boolean; [key: string]: unknown}) {
		if (content.allLocales) {
			content.allLocales = false;
		}

		this.parseLocalizedContents(content);
	}

	/**
	 * Returns contents
	 * @param {String} id Content Id
	 * @param {Object} config Additional config
	 * @returns {Promise}
	 */
	async getContents(id, config: TEntriesConfig = {}): Promise<unknown> {
		return await this.getContent(id, config, true);
	}

	/**
	 * Returns Components Builder
	 * @returns {ComponentsBuilder}
	 */
	getBuilder(): ComponentsBuilder {
		return this.componentsBuilder;
	}

	/**
	 * Has Contentful a preview widget?
	 * @returns {boolean} always true for Contentful
	 */
	hasPreviewWidget() {
		return true;
	}
}

export default ContentHubV2Provider;
